import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Roving-tabindex grid navigation for D-pad / arrow keys.
 *
 * Standard "ChannelGrid"-style problem: 200+ tile <button>s in a
 * responsive grid. Default tab behavior would walk through every tile
 * one at a time which is unusable on a remote. Roving-tabindex pattern
 * (per WAI-ARIA): only ONE tile has tabIndex=0 at a time; arrow keys
 * move focus to siblings, which become tabIndex=0 in turn.
 *
 * Usage:
 *   const grid = useRovingFocus({ count, columns });
 *   <div onKeyDown={grid.onKeyDown}>
 *     {items.map((item, i) => (
 *       <button
 *         ref={(el) => grid.itemRefs.current[i] = el}
 *         tabIndex={grid.activeIndex === i ? 0 : -1}
 *         onFocus={() => grid.setActiveIndex(i)}
 *       >...</button>
 *     ))}
 *   </div>
 *
 * `columns` is the layout's column count — drives how Up/Down jump
 * (jump by `columns`, not by 1). Pass a number that updates with the
 * viewport (e.g., from a useMediaQuery hook) so jumps stay correct
 * after a resize.
 *
 * Wrapping: arrow keys clamp at edges by default; pass wrap: true to
 * loop. Most TV-app grids don't wrap (it's confusing) so we default
 * to clamp.
 *
 * Home / End: jump to first / last tile. PageUp / PageDown: jump 5
 * rows at a time (bigger leaps for a 1000-channel list).
 */

interface Options {
  count: number;
  columns: number;
  wrap?: boolean;
  /** Optional initial index (default 0). Useful when restoring focus
   * after a route change. */
  initial?: number;
}

export function useRovingFocus({ count, columns, wrap = false, initial = 0 }: Options) {
  const [activeIndex, setActiveIndex] = useState(Math.min(initial, Math.max(0, count - 1)));
  const itemRefs = useRef<Array<HTMLElement | null>>([]);

  // Keep activeIndex in range when count changes (filters, paging).
  useEffect(() => {
    if (activeIndex >= count) setActiveIndex(Math.max(0, count - 1));
  }, [count, activeIndex]);

  // After activeIndex changes via keyboard, .focus() the new item.
  // We don't focus on every render — only when explicitly moved by
  // a key event — so initial mount doesn't steal focus.
  const programmaticFocusRef = useRef(false);
  useEffect(() => {
    if (programmaticFocusRef.current) {
      itemRefs.current[activeIndex]?.focus();
      programmaticFocusRef.current = false;
    }
  }, [activeIndex]);

  const move = useCallback(
    (delta: number) => {
      setActiveIndex((cur) => {
        let next = cur + delta;
        if (wrap) {
          next = ((next % count) + count) % count;
        } else {
          next = Math.max(0, Math.min(count - 1, next));
        }
        programmaticFocusRef.current = next !== cur;
        return next;
      });
    },
    [count, wrap],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight': move(1); e.preventDefault(); break;
        case 'ArrowLeft':  move(-1); e.preventDefault(); break;
        case 'ArrowDown':  move(columns); e.preventDefault(); break;
        case 'ArrowUp':    move(-columns); e.preventDefault(); break;
        case 'Home':       setActiveIndex(0); programmaticFocusRef.current = true; e.preventDefault(); break;
        case 'End':        setActiveIndex(Math.max(0, count - 1)); programmaticFocusRef.current = true; e.preventDefault(); break;
        case 'PageDown':   move(columns * 5); e.preventDefault(); break;
        case 'PageUp':     move(-columns * 5); e.preventDefault(); break;
      }
    },
    [columns, count, move],
  );

  return { activeIndex, setActiveIndex, itemRefs, onKeyDown };
}
